/**
 * Toucan Reads — the hash routes. The library is the app's home, narrowed to
 * one list or to the unfiled ones. The reader, the edit page, Add, the import,
 * Manage lists and Settings are pages rendered in `main`, so the back gesture
 * leaves each of them. The new-list dialog is modal, with no route.
 */

import { goToPath, hashSegments } from "toucan";

// The platform handles the hash itself, and this app reads the routes off it.
export { goToPath as go };

/** Every page the hash can name. */
export type Route =
  | { name: "library" }
  | { name: "list"; id: string }
  | { name: "unfiled" }
  | { name: "edit"; id: string }
  | { name: "read"; id: string }
  | { name: "add" }
  | { name: "import" }
  | { name: "lists" }
  | { name: "settings" };

/** The routes that show the library, whole or narrowed to a list or none. */
export type LibraryRoute = Extract<
  Route,
  { name: "library" | "list" | "unfiled" }
>;

export function currentRoute(): Route {
  const [name, id] = hashSegments();
  if (name === "edit" && id !== undefined) {
    return { name: "edit", id };
  }
  if (name === "read" && id !== undefined) {
    return { name: "read", id };
  }
  if (name === "list" && id !== undefined) {
    return { name: "list", id };
  }
  if (name === "unfiled") {
    return { name: "unfiled" };
  }
  if (name === "add") {
    return { name: "add" };
  }
  if (name === "import") {
    return { name: "import" };
  }
  if (name === "lists") {
    return { name: "lists" };
  }
  if (name === "settings") {
    return { name: "settings" };
  }
  return { name: "library" };
}

/** Which item a route is about, for the two that name one. */
export function routeItemId(route: Route): string | undefined {
  return route.name === "edit" || route.name === "read" ? route.id : undefined;
}

/**
 * The library view last shown. A page opened by a shared link rather than from
 * the library has never seen one, so it stays the whole library.
 */
let libraryLeft: LibraryRoute = { name: "library" };

/** Whether a route is one of the library's own. */
export function isLibraryRoute(route: Route): route is LibraryRoute {
  return (
    route.name === "library" ||
    route.name === "list" ||
    route.name === "unfiled"
  );
}

/**
 * The reading a share sheet arrived over. Leaving the shared link's page goes
 * back to that reading rather than to the library, once, and only while the
 * library has not been shown since.
 */
let interrupted: string | undefined;

/**
 * Note a library view as the one to come back to, if this route is one, and
 * forget any reading a share interrupted.
 */
export function noteLibraryRoute(route: Route): void {
  if (isLibraryRoute(route)) {
    libraryLeft = route;
    interrupted = undefined;
  }
}

export function noteInterruptedReading(id: string): void {
  interrupted = id;
}

/**
 * The item a save has just made, whose edit page opens with the cursor in its
 * note, since a note is what a person saving a link has left to add.
 */
let justSaved: string | undefined;

export function noteJustSaved(id: string): void {
  justSaved = id;
}

/** Whether `id` is the item just saved, which it stops being once asked. */
export function takeJustSaved(id: string): boolean {
  const saved = justSaved === id;
  justSaved = undefined;
  return saved;
}

/** The library view a sub-page was opened from, where leaving it goes. */
export function libraryPath(): string {
  if (libraryLeft.name === "list") {
    return `list/${libraryLeft.id}`;
  }
  return libraryLeft.name === "unfiled" ? "unfiled" : "";
}

/**
 * Where leaving a sub-page goes. That is the reading a share interrupted, if
 * one did, and otherwise {@link libraryPath}.
 */
export function leavePath(): string {
  const reading = interrupted;
  interrupted = undefined;
  return reading === undefined ? libraryPath() : `read/${reading}`;
}

/**
 * The list the library was narrowed to, or undefined for the whole library or
 * the unfiled ones: the list an item saved from there is filed on.
 */
export function libraryListId(): string | undefined {
  return libraryLeft.name === "list" ? libraryLeft.id : undefined;
}

/**
 * Which list the library is narrowed to: a list's id, `""` for the unfiled
 * ones, or null for the whole library.
 */
export function routeListId(route: Route): string | null {
  if (route.name === "list") {
    return route.id;
  }
  return route.name === "unfiled" ? "" : null;
}

/**
 * Forget a just-deleted list as the view to come back to, which
 * `libraryPath()` would otherwise go to.
 */
export function forgetLibraryList(listId: string): void {
  if (libraryLeft.name === "list" && libraryLeft.id === listId) {
    libraryLeft = { name: "library" };
  }
}

/** The hash a list filter is addressed by. */
export function listPath(listId: string | null): string {
  if (listId === null) {
    return "";
  }
  return listId === "" ? "unfiled" : `list/${listId}`;
}
